import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, getAdminToken, setAdminToken } from "../lib/adminApi";
import { formatINR } from "../lib/api";
import { toast } from "sonner";
import { LogOut, RefreshCcw } from "lucide-react";

const STATUSES = [
    "confirmed",
    "packed",
    "in_transit",
    "delivered",
    "cancelled",
];

const STATUS_LABEL = {
    confirmed: "Confirmed",
    packed: "Packed",
    in_transit: "In transit",
    delivered: "Delivered",
    cancelled: "Cancelled",
};

export default function Admin() {
    const nav = useNavigate();
    const [stats, setStats] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");

    useEffect(() => {
        if (!getAdminToken()) {
            nav("/admin/login");
            return;
        }
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refresh = async () => {
        setLoading(true);
        try {
            const [s, o] = await Promise.all([
                adminApi.get("/stats"),
                adminApi.get("/orders"),
            ]);
            setStats(s.data);
            setOrders(o.data);
        } catch (err) {
            if (err.response?.status === 401) {
                setAdminToken(null);
                nav("/admin/login");
            } else {
                toast.error("Could not load admin data.");
            }
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (order_number, status) => {
        try {
            await adminApi.patch(`/orders/${order_number}`, { status });
            setOrders((prev) =>
                prev.map((o) =>
                    o.order_number === order_number
                        ? { ...o, status }
                        : o,
                ),
            );
            toast.success(`${order_number} → ${STATUS_LABEL[status]}`);
        } catch {
            toast.error("Update failed.");
        }
    };

    const signOut = () => {
        setAdminToken(null);
        nav("/admin/login");
    };

    const visible =
        filter === "all"
            ? orders
            : orders.filter((o) => o.status === filter);

    return (
        <div
            className="max-w-[1440px] mx-auto px-6 md:px-12 pt-12 pb-24"
            data-testid="admin-page"
        >
            <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
                <div>
                    <div className="overline">Veloura · Atelier console</div>
                    <h1
                        className="font-serif text-4xl sm:text-5xl tracking-tight mt-2"
                        style={{ color: "var(--vl-text)" }}
                    >
                        Operations.
                    </h1>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={refresh}
                        className="vl-btn vl-btn-ghost"
                        data-testid="admin-refresh"
                        disabled={loading}
                    >
                        <RefreshCcw size={14} strokeWidth={1.5} />
                        {loading ? "Loading…" : "Refresh"}
                    </button>
                    <button
                        onClick={signOut}
                        className="vl-btn vl-btn-ghost"
                        data-testid="admin-signout"
                    >
                        <LogOut size={14} strokeWidth={1.5} />
                        Sign out
                    </button>
                </div>
            </div>

            {/* Stats cards */}
            {stats && (
                <div
                    className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10"
                    data-testid="admin-stats"
                >
                    {[
                        { k: "Orders", v: stats.total_orders },
                        {
                            k: "Revenue",
                            v: formatINR(stats.revenue_inr),
                        },
                        { k: "Paid", v: stats.paid_orders },
                        { k: "COD", v: stats.cod_orders },
                        {
                            k: "Pending payment",
                            v: stats.pending_payment,
                        },
                        {
                            k: "Newsletter",
                            v: stats.newsletter_subscribers,
                        },
                        {
                            k: "Contact messages",
                            v: stats.contact_messages,
                        },
                    ].map((s) => (
                        <div
                            key={s.k}
                            className="p-5"
                            style={{
                                background: "var(--vl-surface)",
                                border: "1px solid var(--vl-border)",
                            }}
                        >
                            <div className="overline">{s.k}</div>
                            <div
                                className="font-serif text-2xl mt-2"
                                style={{ color: "var(--vl-text)" }}
                            >
                                {s.v}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-4">
                <button
                    onClick={() => setFilter("all")}
                    className="text-[11px] tracking-[0.22em] uppercase px-4 py-2"
                    style={{
                        border: "1px solid var(--vl-border)",
                        color: filter === "all" ? "var(--vl-bg)" : "var(--vl-text)",
                        background:
                            filter === "all"
                                ? "var(--vl-rose)"
                                : "transparent",
                    }}
                    data-testid="admin-filter-all"
                >
                    All
                </button>
                {STATUSES.map((s) => (
                    <button
                        key={s}
                        onClick={() => setFilter(s)}
                        className="text-[11px] tracking-[0.22em] uppercase px-4 py-2"
                        style={{
                            border: "1px solid var(--vl-border)",
                            color:
                                filter === s
                                    ? "var(--vl-bg)"
                                    : "var(--vl-text)",
                            background:
                                filter === s
                                    ? "var(--vl-rose)"
                                    : "transparent",
                        }}
                        data-testid={`admin-filter-${s}`}
                    >
                        {STATUS_LABEL[s]}
                    </button>
                ))}
            </div>

            {/* Orders table */}
            <div
                className="overflow-x-auto"
                style={{
                    background: "var(--vl-surface)",
                    border: "1px solid var(--vl-border)",
                }}
                data-testid="admin-orders-table"
            >
                <table className="w-full text-sm">
                    <thead>
                        <tr
                            className="overline"
                            style={{
                                background: "var(--vl-bg)",
                                color: "var(--vl-muted)",
                            }}
                        >
                            <th className="text-left p-4">Order</th>
                            <th className="text-left p-4">Customer</th>
                            <th className="text-left p-4">City</th>
                            <th className="text-left p-4">Payment</th>
                            <th className="text-left p-4">Total</th>
                            <th className="text-left p-4">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((o) => (
                            <tr
                                key={o.order_number}
                                style={{
                                    borderTop:
                                        "1px solid var(--vl-border)",
                                }}
                                data-testid={`admin-row-${o.order_number}`}
                            >
                                <td
                                    className="p-4"
                                    style={{ color: "var(--vl-text)" }}
                                >
                                    <div className="font-serif text-lg">
                                        {o.order_number}
                                    </div>
                                    <div
                                        className="text-[11px] mt-1"
                                        style={{
                                            color: "var(--vl-muted)",
                                        }}
                                    >
                                        {new Date(
                                            o.created_at,
                                        ).toLocaleString("en-IN")}
                                    </div>
                                </td>
                                <td
                                    className="p-4"
                                    style={{ color: "var(--vl-text)" }}
                                >
                                    <div>{o.full_name}</div>
                                    <div
                                        className="text-[11px] mt-1"
                                        style={{
                                            color: "var(--vl-muted)",
                                        }}
                                    >
                                        {o.email}
                                    </div>
                                </td>
                                <td
                                    className="p-4"
                                    style={{ color: "var(--vl-muted)" }}
                                >
                                    {o.city}, {o.state} · {o.pincode}
                                </td>
                                <td
                                    className="p-4"
                                    style={{ color: "var(--vl-text)" }}
                                >
                                    <div>
                                        {o.payment_method === "cod"
                                            ? "COD"
                                            : "Razorpay"}
                                    </div>
                                    <div
                                        className="text-[11px] mt-1 tracking-[0.18em] uppercase"
                                        style={{
                                            color:
                                                o.payment_status === "paid"
                                                    ? "var(--vl-rose)"
                                                    : "var(--vl-muted)",
                                        }}
                                    >
                                        {o.payment_status}
                                    </div>
                                </td>
                                <td
                                    className="p-4 font-serif text-lg"
                                    style={{ color: "var(--vl-text)" }}
                                >
                                    {formatINR(o.total)}
                                </td>
                                <td className="p-4">
                                    <select
                                        value={o.status}
                                        onChange={(e) =>
                                            updateStatus(
                                                o.order_number,
                                                e.target.value,
                                            )
                                        }
                                        className="bg-transparent border px-3 py-2 text-sm"
                                        style={{
                                            borderColor:
                                                "var(--vl-border)",
                                            color: "var(--vl-text)",
                                        }}
                                        data-testid={`admin-status-${o.order_number}`}
                                    >
                                        {STATUSES.map((s) => (
                                            <option
                                                key={s}
                                                value={s}
                                                style={{
                                                    background:
                                                        "var(--vl-bg)",
                                                }}
                                            >
                                                {STATUS_LABEL[s]}
                                            </option>
                                        ))}
                                    </select>
                                </td>
                            </tr>
                        ))}
                        {visible.length === 0 && !loading && (
                            <tr>
                                <td
                                    colSpan={6}
                                    className="p-8 text-center"
                                    style={{ color: "var(--vl-muted)" }}
                                >
                                    No orders in this view.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
